import { useParams } from "react-router";

import {
  FeatureFlag,
  useFeatureFlag,
} from "@vega-api/extended/auth/useFeatureFlag";
import { useGetDealGeneralPartnerViewV3 } from "@vega-api/generated/deal/deal";
import { DealStatusEnum } from "@vega-api/generated/types/dealStatusEnum";

/**
 * Closed and cancelled co-invest deals are read-only in the GP portal (UI
 * guard only — the backend remains the authority for rejecting mutations).
 * Closed deals keep the deal-shortlist notes column editable; cancelled
 * deals lock everything.
 */
export const useCoInvestDealReadOnly = () => {
  const { id = "" } = useParams<{ id: string }>();
  const { featureFlag } = useFeatureFlag();
  const isFlagEnabled = featureFlag[FeatureFlag.COINVEST_REPORTING_VD_26152];

  const {
    data: dealStatus,
    isFetched: isStatusFetched,
    isError: isStatusError,
  } = useGetDealGeneralPartnerViewV3(id, {
    query: {
      enabled: !!id && isFlagEnabled,
      select: (response) => response.data?.deal.status,
    },
  });

  const isClosedDeal = isFlagEnabled && dealStatus === DealStatusEnum.CLOSED;
  const isCancelledDeal =
    isFlagEnabled && dealStatus === DealStatusEnum.CANCELLED;
  const isReadOnlyDeal = isClosedDeal || isCancelledDeal;
  const isResolved = !isFlagEnabled || (isStatusFetched && !isStatusError);

  return {
    // Unresolved counts as not-closed/not-cancelled → use for UI guard and redirects
    isClosedDeal,
    isCancelledDeal,
    isReadOnlyDeal,
    isResolved,
    // Unresolved counts as locked → use for mutation guards
    isMutationLocked: !isResolved || isReadOnlyDeal,
  };
};
